/**
 * FaceSafeImage — Iter-297d — Portrait-safe image rendering.
 *
 * The Creator Network and AI Ambassador hero/cover/avatar slots used
 * `object-cover` with the default `center center` origin. For
 * portrait talent photos (face in the upper third) that crop sliced
 * eyes/foreheads off, especially on narrow mobile viewports and in
 * circular avatars. The user reported this as a recurring P1.
 *
 * Fix: `object-cover` with a 50%/25% origin keeps the face safe area
 * visible regardless of aspect ratio. A graceful fallback covers
 * missing / 4xx URLs by rendering a deterministic ui-avatars seed
 * derived from `alt` text — so cards never show a broken-image icon.
 */
import React, { useState } from 'react';

function makeFallback(label, size = 256, bg = '6366f1') {
  const seed = (label || 'Talent').slice(0, 24);
  return `https://ui-avatars.com/api/?name=${encodeURIComponent(seed)}&size=${size}&background=${bg}&color=fff&bold=true`;
}

export default function FaceSafeImage({
  src,
  alt,
  className = '',
  // CSS object-position. `50% 25%` keeps the face's safe area visible
  // for the vast majority of headshots without distorting full-body
  // shots. Callers can override (e.g. `50% 15%` for very tight crops).
  facePosition = '50% 25%',
  fallbackSize = 256,
  fallbackBg = '6366f1',
  rounded = false,
  ...rest
}) {
  const [errored, setErrored] = useState(false);
  const finalSrc = !src || errored ? makeFallback(alt, fallbackSize, fallbackBg) : src;
  return (
    <img
      src={finalSrc}
      alt={alt || ''}
      onError={() => setErrored(true)}
      style={{ objectPosition: facePosition }}
      className={`object-cover ${rounded ? 'rounded-full' : ''} ${className}`}
      loading="lazy"
      decoding="async"
      {...rest}
    />
  );
}
